import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

// ── Thunks ──────────────────────────────────────────────────
export const get_admin_campaigns = createAsyncThunk(
    'adminCampaign/list',
    async ({ page = 1, limit = 20, status = '', search = '' } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const params = new URLSearchParams()
            params.set('page', page)
            params.set('limit', limit)
            if (status) params.set('status', status)
            if (search) params.set('search', search)
            const { data } = await api.get(`/admin/campaigns?${params.toString()}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const create_campaign = createAsyncThunk(
    'adminCampaign/create',
    async (payload, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post('/admin/campaigns', payload)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const get_campaign = createAsyncThunk(
    'adminCampaign/getOne',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/admin/campaigns/${id}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const update_campaign = createAsyncThunk(
    'adminCampaign/update',
    async ({ id, ...payload }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.patch(`/admin/campaigns/${id}`, payload)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const delete_campaign = createAsyncThunk(
    'adminCampaign/delete',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.delete(`/admin/campaigns/${id}`)
            return fulfillWithValue({ id, message: data.message })
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const update_campaign_status = createAsyncThunk(
    'adminCampaign/updateStatus',
    async ({ id, status }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.patch(`/admin/campaigns/${id}/status`, { status })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const reorder_campaign_items = createAsyncThunk(
    'adminCampaign/reorderItems',
    async ({ id, order }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.patch(`/admin/campaigns/${id}/reorder-items`, { order })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// Lightweight product search used by the campaign product picker.
export const search_admin_products = createAsyncThunk(
    'adminCampaign/searchProducts',
    async ({ q = '', page = 1, limit = 20 } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const params = new URLSearchParams({ q, page: String(page), limit: String(limit) })
            const { data } = await api.get(`/admin/products/search?${params.toString()}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// ── Slice ───────────────────────────────────────────────────
const adminCampaignSlice = createSlice({
    name: 'adminCampaign',
    initialState: {
        campaigns:       [],
        total:           0,
        page:            1,
        totalPages:      1,
        currentCampaign: null,
        searchResults:   [],
        searchLoader:    false,
        loader:          false,
        submitLoader:    false,
        successMessage:  '',
        errorMessage:    '',
    },
    reducers: {
        messageClear: (s) => { s.successMessage = ''; s.errorMessage = '' },
        clearCurrent: (s) => { s.currentCampaign = null; s.searchResults = [] },
    },
    extraReducers: (builder) => {
        builder
            .addCase(get_admin_campaigns.pending,   (s) => { s.loader = true })
            .addCase(get_admin_campaigns.fulfilled, (s, { payload }) => {
                s.loader = false
                s.campaigns = payload.campaigns || []
                s.total = payload.total || 0
                s.page = payload.page || 1
                s.totalPages = payload.totalPages || 1
            })
            .addCase(get_admin_campaigns.rejected,  (s) => { s.loader = false })

            .addCase(create_campaign.pending,   (s) => { s.submitLoader = true; s.errorMessage = '' })
            .addCase(create_campaign.fulfilled, (s, { payload }) => {
                s.submitLoader = false
                s.successMessage = payload.message || 'Yaradıldı'
                s.currentCampaign = payload.campaign || null
            })
            .addCase(create_campaign.rejected,  (s, { payload }) => {
                s.submitLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Xəta baş verdi'
            })

            .addCase(get_campaign.fulfilled, (s, { payload }) => { s.currentCampaign = payload.campaign })

            .addCase(update_campaign.pending,   (s) => { s.submitLoader = true; s.errorMessage = '' })
            .addCase(update_campaign.fulfilled, (s, { payload }) => {
                s.submitLoader = false
                s.successMessage = payload.message || 'Yeniləndi'
                s.currentCampaign = payload.campaign || s.currentCampaign
                const idx = s.campaigns.findIndex(c => c._id === payload.campaign?._id)
                if (idx !== -1) s.campaigns[idx] = payload.campaign
            })
            .addCase(update_campaign.rejected,  (s, { payload }) => {
                s.submitLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Yenilənmədi'
            })

            .addCase(delete_campaign.fulfilled, (s, { payload }) => {
                s.successMessage = payload.message || 'Silindi'
                s.campaigns = s.campaigns.filter(c => c._id !== payload.id)
            })
            .addCase(delete_campaign.rejected,  (s, { payload }) => {
                s.errorMessage = payload?.error || payload?.message || 'Silinmədi'
            })

            .addCase(update_campaign_status.fulfilled, (s, { payload }) => {
                s.successMessage = payload.message || 'Status yeniləndi'
                const idx = s.campaigns.findIndex(c => c._id === payload.campaign?._id)
                if (idx !== -1) s.campaigns[idx] = payload.campaign
                if (s.currentCampaign?._id === payload.campaign?._id) s.currentCampaign = payload.campaign
            })
            .addCase(update_campaign_status.rejected,  (s, { payload }) => {
                s.errorMessage = payload?.error || payload?.message || 'Status yenilənmədi'
            })

            .addCase(reorder_campaign_items.fulfilled, (s, { payload }) => {
                s.successMessage = payload.message || 'Sıralama yeniləndi'
                if (s.currentCampaign && Array.isArray(payload.items)) {
                    s.currentCampaign = { ...s.currentCampaign, items: payload.items }
                }
            })
            .addCase(reorder_campaign_items.rejected,  (s, { payload }) => {
                s.errorMessage = payload?.error || payload?.message || 'Sıralama yenilənmədi'
            })

            .addCase(search_admin_products.pending,   (s) => { s.searchLoader = true })
            .addCase(search_admin_products.fulfilled, (s, { payload }) => {
                s.searchLoader = false
                s.searchResults = payload.products || []
            })
            .addCase(search_admin_products.rejected,  (s) => { s.searchLoader = false })
    },
})

export const { messageClear, clearCurrent } = adminCampaignSlice.actions
export default adminCampaignSlice.reducer
